// src/helpers/getChallengeTabMessage.js

const DEFAULT_MEMBER_MESSAGE = [
    { text: 'Insider, Elite,', bold: true },
    { text: ' and ' },
    { text: 'VIP members,', bold: true },
    { text: ' earn Rewards points or a $5 CashPass for every Challenge completed!' },
];

/**
 * Returns the contextual message for a given tab, tier, and challenge state.
 * @param {string} activeTab - 'inProgress' | 'notStarted' | 'completed' | 'expired'
 * @param {string} tier - 'Member' | 'Insider' | 'Elite' | 'VIP'
 * @param {object} state - { completed, limit, inProgressCount, notStartedCount }
 * @param {object} [preferences] - Optional preferences object
 * @returns {object|null} { type: 'info'|'congrats', text: string | Array<{ text: string, bold?: boolean }> }
 */
export const getChallengeTabMessage = (activeTab, tier, state, preferences = {}) => {
    const { completed, limit, inProgressCount, notStartedCount } = state;
    const nextTier = { Insider: 'Elite', Elite: 'VIP', VIP: null };
    const defaultNextTierLimit = { Insider: 4, Elite: 6, VIP: 6 };
    const nextTierLimit = preferences.challengeNextTierLimit || defaultNextTierLimit;
    const additionalChallenges = (nextTierLimit[tier] || 0) - limit;

    if (tier === 'Member') {
        return {
            type: 'info',
            text: preferences.challengeMemberMessage || DEFAULT_MEMBER_MESSAGE,
        };
    }

    switch (activeTab) {
        case 'inProgress': {
            if (inProgressCount === 0 && completed === 0) {
                return {
                    type: 'info',
                    text:
                        'You haven\'t started any challenges yet. Activate each challenge from the "Not Started" tab before making a purchase.',
                };
            }
            if (limit > 0 && completed >= limit) {
                if (tier === 'VIP') {
                    return {
                        type: 'congrats',
                        text: [
                            { text: 'CONGRATS!', bold: true },
                            {
                                text: ` — You completed all ${completed} of your challenges. Challenge limit will reset the beginning of the new year.`,
                            },
                        ],
                    };
                }
                return {
                    type: 'congrats',
                    text: [
                        { text: 'CONGRATS!', bold: true },
                        {
                            text: ` — You completed all ${completed} of your challenges. Level up to ${nextTier[tier]} tier to unlock ${additionalChallenges} more.`,
                        },
                    ],
                };
            }
            if (completed > 0 && completed < limit) {
                return {
                    type: 'info',
                    text: [
                        { text: 'You\'re making great progress!', bold: true },
                        { text: ' Activate more challenges from the ' },
                        { text: '"Not Started"', bold: true },
                        { text: ' tab to continue earning rewards.' },
                    ],
                };
            }
            return null;
        }
        case 'notStarted': {
            if (limit > 0 && completed >= limit) {
                if (tier === 'VIP') {
                    return {
                        type: 'congrats',
                        text: [
                            { text: 'CONGRATS!', bold: true },
                            {
                                text: ` — You completed all ${completed} of your challenges. Challenge limits will reset at the beginning of the new year.`,
                            },
                        ],
                    };
                }
                return {
                    type: 'congrats',
                    text: [
                        { text: 'CONGRATS!', bold: true },
                        {
                            text: ` — You completed all ${completed} of your challenges. Level up to the next tier to unlock ${additionalChallenges} more.`,
                        },
                    ],
                };
            }
            if (limit > 0 && (state.active || 0) + completed >= limit) return null;
            if (notStartedCount === 0) {
                if (tier === 'VIP') {
                    return { type: 'info', text: "You're making progress on all your challenges!" };
                }
                return {
                    type: 'info',
                    text: `You're making progress on all your challenges! Advance to the next tier to unlock ${additionalChallenges} more.`,
                };
            }
            if (notStartedCount > 0) {
                return {
                    type: 'info',
                    text: [
                        { text: `${tier.toUpperCase()}`, bold: true },
                        {
                            text: ` tier members may choose up to ${limit} challenges below. Accept your challenges before you begin shopping.`,
                        },
                    ],
                };
            }
            return null;
        }
        case 'completed': {
            if (completed === 0) {
                return {
                    type: 'info',
                    text: [
                        { text: 'Your completed challenges will appear here', bold: true },
                        { text: ' — Finish a challenge to start building your accomplishments.' },
                    ],
                };
            }
            if (limit > 0 && completed >= limit) {
                if (tier === 'VIP') {
                    return {
                        type: 'congrats',
                        text: [
                            { text: 'CONGRATS!', bold: true },
                            {
                                text: ` — You completed all ${completed} of your challenges. Challenge limits will reset at the beginning of the new year.`,
                            },
                        ],
                    };
                }
                return {
                    type: 'congrats',
                    text: [
                        { text: 'CONGRATS!', bold: true },
                        {
                            text: ` — You completed all ${completed} of your challenges. Level up to the next tier to unlock ${additionalChallenges} more.`,
                        },
                    ],
                };
            }
            return null;
        }
        case 'expired':
            return null;
        default:
            return null;
    }
};
